import React, { useState, useEffect } from 'react';
import { submitFormResponse, verifyOtp } from '../../services/api/formApi';
import QuestionResponse from './QuestionResponse';

// Helpers for storing file upload responses in separate cookies with native browser TTL (1 hour)
const setFileUploadCookie = (formId, questionId, url, maxAgeSeconds = 600) => {
  const name = `form_${formId}_q_${questionId}_file`;
  if (url) {
    document.cookie = `${name}=${encodeURIComponent(url)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
  } else {
    document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
  }
};

const getFileUploadCookie = (formId, questionId) => {
  const name = `form_${formId}_q_${questionId}_file=`;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let c = cookies[i].trim();
    if (c.indexOf(name) === 0) {
      return decodeURIComponent(c.substring(name.length));
    }
  }
  return null;
};

const FormResponse = ({ form, userEmail, onSwitchAccount }) => {
  const [responses, setResponses] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP State
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Scheduled Closure Countdown State
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!form.scheduledCloseAt) return;

    const calculateTimeLeft = () => {
      const closeTime = new Date(form.scheduledCloseAt).getTime();
      const now = new Date().getTime();
      const diff = closeTime - now;

      if (diff <= 0) {
        // Optionally handle expired state if not already handled by backend check
        setTimeLeft('Closing soon...');
        return;
      }

      // Calculate days, hours, minutes
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      let timeString = '';
      if (days > 0) timeString += `${days} day${days > 1 ? 's' : ''} `;
      if (hours > 0) timeString += `${hours} hour${hours > 1 ? 's' : ''} `;
      if (minutes > 0 || (days === 0 && hours === 0)) timeString += `${minutes} minute${minutes !== 1 ? 's' : ''}`;

      setTimeLeft(timeString.trim());
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [form.scheduledCloseAt]);

  // Check for previous submission on mount
  useEffect(() => {
    const isSubmittedLocally = localStorage.getItem(`form_${form.id}_submitted`) === 'true';
    if (isSubmittedLocally) {
      setSubmitted(true);
      return;
    }

    // Only verify persistence if we are restricted to one response per email
    if (form.settings?.collectEmail && !form.settings?.allowMultipleResponses) {
      const submittedEmail = localStorage.getItem(`form_${form.id}_submitted_email`);
      if (userEmail && submittedEmail === userEmail) {
        setSubmitted(true);
      }
    }
  }, [form, userEmail]);

  // Initialize responses from localStorage (or TTL cookies for FILE_UPLOAD)
  useEffect(() => {
    const savedResponses = localStorage.getItem(`form_${form.id}_responses`);
    let parsed = {};
    if (savedResponses) {
      try {
        parsed = JSON.parse(savedResponses) || {};
      } catch (e) {
        parsed = {};
      }
    }

    const initialResponses = {};
    form.questions.forEach(question => {
      if (question.type === 'FILE_UPLOAD') {
        // Read file upload URL from separate TTL cookie (keyed by formId & questionId)
        const fileUrl = getFileUploadCookie(form.id, question.id);
        initialResponses[question.id] = {
          questionId: question.id,
          type: question.type,
          value: fileUrl || null,
        };
      } else if (parsed[question.id]) {
        initialResponses[question.id] = parsed[question.id];
      } else {
        initialResponses[question.id] = {
          questionId: question.id,
          type: question.type,
          value: null,
        };
      }
    });
    setResponses(initialResponses);
  }, [form]);

  // Save responses to localStorage whenever they change (FILE_UPLOAD questions saved as null in localStorage)
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      const responsesToSave = {};
      Object.entries(responses).forEach(([qId, resp]) => {
        if (resp.type === 'FILE_UPLOAD') {
          responsesToSave[qId] = { ...resp, value: null };
        } else {
          responsesToSave[qId] = resp;
        }
      });
      localStorage.setItem(`form_${form.id}_responses`, JSON.stringify(responsesToSave));
    }
  }, [responses, form.id]);

  const updateResponse = (questionId, value) => {
    setResponses(prev => {
      const qType = prev[questionId]?.type;
      if (qType === 'FILE_UPLOAD') {
        // Update separate TTL cookie for file upload question
        setFileUploadCookie(form.id, questionId, value);
      }
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          value,
        },
      };
    });

    // Clear validation error for this question
    if (validationErrors[questionId]) {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: undefined,
      }));
    }
  };

  const validateResponses = () => {
    const errors = {};

    form.questions.forEach(question => {
      const response = responses[question.id];
      if (!response) return;

      // Check required fields
      if (question.required && (!response.value || response.value === '' ||
        (Array.isArray(response.value) && response.value.length === 0))) {
        errors[question.id] = 'This question is required';
        return;
      }

      // Type-specific validation
      switch (question.type) {
        case 'SHORT_TEXT':
        case 'PARAGRAPH':
          if (response.value) {
            const length = response.value.length;
            const minLen = question.config?.minLen || 1;
            const maxLen = question.config?.maxLen || (question.type === 'SHORT_TEXT' ? 100 : 1000);

            if (length < minLen) {
              errors[question.id] = `Response must be at least ${minLen} characters`;
            } else if (length > maxLen) {
              errors[question.id] = `Response must be no more than ${maxLen} characters`;
            }
          }
          break;

        case 'MULTIPLE_CHOICE':
          // Already validated by the component
          break;

        case 'CHECKBOX':
          if (response.value && Array.isArray(response.value)) {
            const minSelections = question.config?.minSelections || 1;
            const maxSelections = question.config?.maxSelections || question.config?.options?.length || response.value.length;

            if (response.value.length < minSelections) {
              errors[question.id] = `Please select at least ${minSelections} option${minSelections > 1 ? 's' : ''}`;
            } else if (response.value.length > maxSelections) {
              errors[question.id] = `Please select no more than ${maxSelections} option${maxSelections > 1 ? 's' : ''}`;
            }
          }
          break;

        case 'DROPDOWN':
          // Already validated by the component
          break;

        case 'DATE_PICKER':
          if (response.value) {
            const dateValue = new Date(response.value);
            const minDate = question.config?.minDate ? new Date(question.config.minDate) : null;
            const maxDate = question.config?.maxDate ? new Date(question.config.maxDate) : null;

            if (isNaN(dateValue.getTime())) {
              errors[question.id] = 'Invalid date';
            } else {
              if (minDate && dateValue < minDate) {
                errors[question.id] = `Date must be on or after ${question.config.minDate}`;
              }
              if (maxDate && dateValue > maxDate) {
                errors[question.id] = `Date must be on or before ${question.config.maxDate}`;
              }
            }
          }
          break;

        default:
          break;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearFileUploadCookies = () => {
    form.questions.forEach(question => {
      if (question.type === 'FILE_UPLOAD') {
        setFileUploadCookie(form.id, question.id, null);
      }
    });
  };

  const markAsSubmitted = () => {
    setSubmitted(true);
    localStorage.removeItem(`form_${form.id}_responses`);
    clearFileUploadCookies();
    localStorage.setItem(`form_${form.id}_submitted`, 'true');

    // Persist submission if single response enforced
    if (form.settings?.collectEmail && !form.settings?.allowMultipleResponses && userEmail) {
      localStorage.setItem(`form_${form.id}_submitted_email`, userEmail);
    }
  };

  const handleSubmit = async () => {
    // Check if email is required but missing
    if (form.settings?.collectEmail && !userEmail) {
      setSubmitError('Email address is required for this form. Please switch account to sign in.');
      window.scrollTo(0, 0); // Scroll to top to see error or header
      return;
    }

    if (!validateResponses()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    // Format answers for API
    const answers = Object.entries(responses).map(([questionId, data]) => {
      let formattedResponse = data.value;

      if (!formattedResponse) return null;

      if (['SHORT_TEXT', 'PARAGRAPH'].includes(data.type)) {
        formattedResponse = String(data.value);
      } else if (['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(data.type)) {
        if (!Array.isArray(data.value)) {
          formattedResponse = [String(data.value)];
        } else {
          formattedResponse = data.value.map(String);
        }
      } else if (data.type === 'DATE_PICKER') {
        formattedResponse = String(data.value);
      } else if (data.type === 'FILE_UPLOAD') {
        // The value is already the Cloudinary URL string
        formattedResponse = String(data.value);
      }

      return {
        questionId,
        response: formattedResponse
      };
    }).filter(Boolean);

    const payload = {
      answers,
      submitterEmail: userEmail || undefined
    };

    try {
      const response = await submitFormResponse(form.id, payload);

      if (response.requireOtp) {
        setVerificationToken(response.token);
        setShowOtpInput(true);
      } else {
        markAsSubmitted();
      }
    } catch (err) {
      console.error('Submission error:', err);
      setSubmitError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;

    setIsVerifying(true);
    setSubmitError(null);

    try {
      await verifyOtp({
        token: verificationToken,
        otp: otp.trim()
      });

      setShowOtpInput(false);
      markAsSubmitted();
    } catch (err) {
      console.error('OTP Verification error:', err);
      setSubmitError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-vh-100 py-5 d-flex align-items-center justify-content-center" style={{ background: '#e0e7ff' }}>
        <div className="bg-white rounded-3 shadow-lg p-5 text-center" style={{ maxWidth: '500px', width: '90%' }}>
          <div className="mb-4 text-success">
            <i className="fa fa-check-circle-o fa-4x" aria-hidden="true"></i>
          </div>
          <h2 className="mb-3 fw-bold font-heading">Response Recorded</h2>
          <p className="text-secondary mb-4">Thank you for filling out <strong>{form.title}</strong>. Your response has been successfully recorded.</p>

          {(!form.settings?.collectEmail || form.settings?.allowMultipleResponses) && (
            <button
              onClick={() => {
                localStorage.removeItem(`form_${form.id}_submitted`);
                setSubmitted(false);
                window.location.reload();
              }}
              className="btn btn-primary px-4"
            >
              Submit another response
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="min-vh-100 py-5" style={{ background: '#e0e7ff' }}>
      <div className="container" style={{ maxWidth: '640px' }}>

        {/* Header Card */}
        <div className="bg-white rounded-3 shadow-md mb-4 overflow-hidden position-relative">
          <div className="position-absolute top-0 start-0 w-100 bg-primary" style={{ height: '10px' }}></div>
          <div className="p-4 pt-5">
            <h1 className="mb-2 fw-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{form.title}</h1>
            {form.description && (
              <p className="text-secondary mb-3">{form.description}</p>
            )}

            {form.scheduledCloseAt && timeLeft && (
              <div className="d-inline-flex align-items-center bg-warning-subtle text-warning-emphasis px-3 py-2 rounded-2 mb-2">
                <i className="fa fa-clock-o me-2"></i>
                <span className="fw-medium small">Closes in {timeLeft}</span>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between pt-3 border-top mt-3">
              {userEmail ? (
                <span className="text-secondary small">
                  <span className="fw-medium text-dark">{userEmail}</span>
                  {onSwitchAccount && (
                    <button
                      onClick={onSwitchAccount}
                      className="btn btn-link p-0 ms-2 text-decoration-none small text-primary align-baseline"
                      style={{ fontSize: 'inherit' }}
                    >
                      (Switch account)
                    </button>
                  )}
                </span>
              ) : (
                <span className="text-secondary small">* Required</span>
              )}
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="d-flex flex-column gap-3 mb-4">
          {form.questions.map((question, index) => (
            <QuestionResponse
              key={question.id}
              question={question}
              questionNumber={index + 1}
              response={responses[question.id]}
              onUpdate={updateResponse}
              error={validationErrors[question.id]}
              formId={form.id}
            />
          ))}

          {form.questions.length === 0 && (
            <div className="bg-white p-5 text-center rounded-3 shadow-sm">
              <p className="text-muted mb-0">Empty form</p>
            </div>
          )}
        </div>

        {/* Submit Actions */}
        {form.questions.length > 0 && (
          <div className="d-flex justify-content-between align-items-center">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary shadow-lg px-5 py-2"
              style={{ minWidth: '120px' }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Sending...
                </>
              ) : (
                'Submit'
              )}
            </button>

            <button
              className="btn btn-link text-secondary text-decoration-none small"
              onClick={() => {
                setResponses({});
                localStorage.removeItem(`form_${form.id}_responses`);
                clearFileUploadCookies();
              }}
            >
              Clear form
            </button>
          </div>
        )}

        {/* OTP Modal */}
        {showOtpInput && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                <div className="modal-header border-bottom-0 p-4 pb-0">
                  <h5 className="modal-title fw-bold">Verify Email</h5>
                </div>
                <div className="modal-body p-4">
                  <p className="text-muted mb-3">
                    We've sent a verification code to <strong>{userEmail}</strong>. Please enter the code below to submit your response.
                  </p>
                  <form onSubmit={handleVerifyOtp}>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control form-control-lg text-center fw-bold letter-spacing-2"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="ENTER OTP"
                        style={{ letterSpacing: '2px', fontSize: '1.5rem' }}
                        autoFocus
                      />
                    </div>
                    {submitError && <div className="alert alert-danger py-2 small border-0">{submitError}</div>}
                    <div className="d-grid mt-4">
                      <button type="submit" className="btn btn-primary btn-lg shadow-sm" disabled={isVerifying}>
                        {isVerifying ? 'Verifying...' : 'Verify & Submit'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* General Error (if not shown in OTP modal) */}
        {submitError && !showOtpInput && (
          <div className="alert alert-danger mt-4 shadow-sm border-0">
            {submitError}
          </div>
        )}

        {hasValidationErrors && (
          <div className="alert alert-danger mt-4 shadow-sm border-0 d-flex align-items-center gap-2">
            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
            <span>Please check the form for errors.</span>
          </div>
        )}

        <div className="text-center mt-5 pb-4">
          <small className="text-muted fw-medium">Powered by FormX</small>
        </div>
      </div>
    </div>
  );
};

export default FormResponse;
